import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Card, Checkbox, Col, Progress, Result, Row, Space, Table, Typography, Upload } from 'antd';
import { CheckSquareOutlined, FolderOutlined, InboxOutlined, ScheduleOutlined, TeamOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ASANA_IMPORT_BATCH, matchAsanaUser, parseAsanaExport, type AsanaExport, type AsanaImportInput, type AsanaImportResult } from '@crm/shared';
import { api, errorMessage } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle, StatCard } from '../../components/StatCard';
import { ProjectSelect } from '../../components/ProjectSelect';
import { useProjectScope } from '../../features/projects/scope';
import { AssigneeSelect, useTaskUsers } from '../tasks/TaskBits';
import { plural } from '../../utils/format';

interface Loaded {
  fileName: string;
  data: AsanaExport;
}

/** Due days end where the importing person is, like due dates picked in the app. */
const endOfDay = (day: string) => dayjs(day).endOf('day').toISOString();

/**
 * Tools → Asana importer: reads an Asana JSON export in the browser, lets you match Asana projects and people to the
 * CRM's, then sends the tasks in batches (parents first). Importing the same file again skips what came in before.
 */
export function AsanaImportPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const scope = useProjectScope();
  const { list: users } = useTaskUsers();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [projectMap, setProjectMap] = useState<Record<string, string | null>>({});
  const [userMap, setUserMap] = useState<Record<string, string | null>>({});
  const [includeCompleted, setIncludeCompleted] = useState(true);
  const [createSections, setCreateSections] = useState(true);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<AsanaImportResult | null>(null);

  const read = async (file: File) => {
    setParseError(null);
    setResult(null);
    try {
      const data = parseAsanaExport(JSON.parse(await file.text()), endOfDay);
      if (!data.tasks.length) throw new Error('The file has no tasks.');
      setLoaded({ fileName: file.name, data });
      // best guesses: projects by name (else the header's project), people by name
      setProjectMap(
        Object.fromEntries(
          data.projects.map((p) => [p.gid, scope.projects.find((x) => x.name.trim().toLowerCase() === p.name.trim().toLowerCase())?.id ?? scope.current?.id ?? null]),
        ),
      );
      setUserMap(Object.fromEntries(data.assignees.map((a) => [a.gid, matchAsanaUser(a.name, users)?.id ?? null])));
    } catch (e) {
      setLoaded(null);
      setParseError(e instanceof SyntaxError ? 'This file is not valid JSON.' : (e as Error).message);
    }
  };

  const tasks = useMemo(() => (loaded ? loaded.data.tasks.filter((t) => includeCompleted || !t.completedAt) : []), [loaded, includeCompleted]);
  const stats = useMemo(() => {
    const all = loaded?.data.tasks ?? [];
    return {
      total: all.length,
      open: all.filter((t) => !t.completedAt).length,
      subtasks: all.filter((t) => t.parentGid).length,
      sections: loaded?.data.sections.length ?? 0,
    };
  }, [loaded]);

  const run = async () => {
    if (!loaded) return;
    const total: AsanaImportResult = { created: 0, skipped: 0, sectionsCreated: 0 };
    setProgress({ done: 0, total: tasks.length });
    try {
      for (let i = 0; i < tasks.length; i += ASANA_IMPORT_BATCH) {
        const body: AsanaImportInput = {
          tasks: tasks.slice(i, i + ASANA_IMPORT_BATCH).map((t) => ({
            gid: t.gid,
            name: t.name,
            notes: t.notes,
            assigneeGid: t.assignee?.gid ?? null,
            completedAt: t.completedAt,
            dueAt: t.dueAt,
            createdAt: t.createdAt,
            projectGid: t.project?.gid ?? null,
            section: t.section,
            parentGid: t.parentGid,
          })),
          projects: scope.enabled ? projectMap : {},
          users: userMap,
          createSections,
        };
        const r = (await api.post<AsanaImportResult>('/tasks/import/asana', body)).data;
        total.created += r.created;
        total.skipped += r.skipped;
        total.sectionsCreated += r.sectionsCreated;
        setProgress({ done: Math.min(i + ASANA_IMPORT_BATCH, tasks.length), total: tasks.length });
      }
      setResult(total);
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task-activity'] });
    } catch (e) {
      // batches already sent stay imported; running it again skips them
      message.error(`Import stopped: ${errorMessage(e)}. Run it again to continue; tasks already imported are skipped.`);
      if (total.created || total.skipped) setResult(total);
    } finally {
      setProgress(null);
    }
  };

  const reset = () => {
    setLoaded(null);
    setResult(null);
    setParseError(null);
  };

  return (
    <div className="page">
      <PageHeader title="Asana importer" subtitle="Bring tasks over from an Asana JSON export, with their sections, subtasks, assignees, due dates and notes." />

      {result ? (
        <Card>
          <Result
            status="success"
            title={`Imported ${plural(result.created, 'task')}`}
            subTitle={[
              result.sectionsCreated ? `${plural(result.sectionsCreated, 'section')} created` : null,
              result.skipped ? `${plural(result.skipped, 'task')} skipped because they were imported before` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            extra={[
              <Link key="open" to="/tasks">
                <Button type="primary">Open tasks</Button>
              </Link>,
              <Button key="again" onClick={reset}>
                Import another file
              </Button>,
            ]}
          />
        </Card>
      ) : !loaded ? (
        <Card>
          <Upload.Dragger
            accept=".json,application/json"
            showUploadList={false}
            beforeUpload={(file) => {
              void read(file);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Drop an Asana JSON export here, or click to choose it</p>
            <p className="ant-upload-hint">In Asana, open a project, click the arrow next to its name, then Export / Print → JSON.</p>
          </Upload.Dragger>
          {parseError && <Alert type="error" showIcon style={{ marginTop: 16 }} message="Could not read the file" description={parseError} />}
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard icon={<CheckSquareOutlined />} label="Tasks" value={stats.total} note={`${stats.open} open · ${stats.total - stats.open} completed`} />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard icon={<ScheduleOutlined />} label="Subtasks" value={stats.subtasks} note="kept under their task" />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard icon={<FolderOutlined />} label="Sections" value={stats.sections} note="besides Asana's untitled one" />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard icon={<TeamOutlined />} label="People" value={loaded.data.assignees.length} note="assigned in Asana" />
            </Col>
          </Row>

          {scope.enabled && loaded.data.projects.length > 0 && (
            <Card title={<CardTitle title="Projects" subtitle="Which CRM project each Asana project's tasks go to." />}>
              <Table
                rowKey="gid"
                size="small"
                pagination={false}
                dataSource={loaded.data.projects}
                columns={[
                  { title: 'Asana project', render: (_, p) => <>{p.name} <Typography.Text type="secondary">· {plural(p.count, 'task')}</Typography.Text></> },
                  {
                    title: 'CRM project',
                    width: 320,
                    render: (_, p) => <ProjectSelect value={projectMap[p.gid] ?? null} onChange={(v) => setProjectMap({ ...projectMap, [p.gid]: v })} style={{ width: '100%' }} />,
                  },
                ]}
              />
            </Card>
          )}

          {loaded.data.assignees.length > 0 && (
            <Card title={<CardTitle title="People" subtitle="Asana assignees and the CRM users who get their tasks. Matched by name where possible; anyone left blank leaves the task unassigned." />}>
              <Table
                rowKey="gid"
                size="small"
                pagination={false}
                dataSource={loaded.data.assignees}
                columns={[
                  { title: 'Asana person', render: (_, a) => <>{a.name || 'Unnamed'} <Typography.Text type="secondary">· {plural(a.count, 'task')}</Typography.Text></> },
                  {
                    title: 'CRM user',
                    width: 320,
                    render: (_, a) => <AssigneeSelect value={userMap[a.gid] ?? null} onChange={(v) => setUserMap({ ...userMap, [a.gid]: v })} placeholder="Unassigned" style={{ width: '100%' }} />,
                  },
                ]}
              />
            </Card>
          )}

          <Card title={<CardTitle title="Import" subtitle={loaded.fileName} />}>
            <Space direction="vertical">
              <Checkbox checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)}>
                Include completed tasks ({stats.total - stats.open})
              </Checkbox>
              <Checkbox checked={createSections} onChange={(e) => setCreateSections(e.target.checked)}>
                Recreate Asana's sections (sections with the same name are reused; otherwise tasks go to No section)
              </Checkbox>
            </Space>
            {progress && <Progress percent={Math.round((progress.done / Math.max(progress.total, 1)) * 100)} style={{ marginTop: 16 }} />}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <Button type="primary" onClick={() => void run()} loading={!!progress} disabled={!tasks.length}>
                Import {plural(tasks.length, 'task')}
              </Button>
              <Button onClick={reset} disabled={!!progress}>
                Choose another file
              </Button>
            </div>
            <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 13 }}>
              Tasks keep their Asana creation and completion dates. Importing the same file again skips tasks that came in before. Followers, tags, likes and
              comments are not in Asana's task export and are not imported.
            </Typography.Paragraph>
          </Card>
        </Space>
      )}
    </div>
  );
}
